import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { SkeletonCards, ErrorState } from "@/components/common/States";
import StatCard from "@/components/common/StatCard";
import LeadTrendChart from "@/components/charts/LeadTrendChart";
import SalespersonChart from "@/components/charts/SalespersonChart";
import PipelineChart from "@/components/charts/PipelineChart";
import BarListChart from "@/components/charts/BarListChart";
import DonutChart from "@/components/charts/DonutChart";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { Contact, GitBranch, Trophy, Activity, Wallet } from "lucide-react";

export default function AnalyticsPanel({ apiParams }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try { const { data } = await api.get("/reports/analytics", { params: apiParams }); setData(data); }
    catch (e) { setError(apiError(e)); }
  }, [apiParams]);
  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <SkeletonCards count={5} />;
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="analytics-total-leads" label="Leads" value={fmtNumber(t.leads)} icon={Contact} tone="teal" />
        <StatCard testid="analytics-total-deals" label="Deals" value={fmtNumber(t.deals)} icon={GitBranch} tone="sky" />
        <StatCard testid="analytics-total-won" label="Won" value={fmtNumber(t.won)} icon={Trophy} tone="emerald" />
        <StatCard testid="analytics-total-activities" label="Activities" value={fmtNumber(t.activities)} icon={Activity} tone="violet" />
        <StatCard testid="analytics-total-revenue" label="Verified revenue" value={fmtCurrency(t.revenue)} icon={Wallet} tone="navy" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <LeadTrendChart data={data.lead_trend} className="lg:col-span-2" testid="analytics-lead-trend" />
        <DonutChart title="Leads by source" data={data.leads_by_source} testid="analytics-lead-source" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarListChart title="Leads by status" subtitle="All 14 standard lead statuses" data={data.leads_by_status} testid="analytics-lead-status" />
        <SalespersonChart data={data.salesperson_comparison} testid="analytics-salesperson" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PipelineChart data={data.pipeline} testid="analytics-pipeline" />
        <BarListChart title="Stage movements" subtitle="Deals moved into each stage" data={data.stage_movements} color="#0284C7" hideZero testid="analytics-stage-moves" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarListChart title="Activities by type" data={data.activities_by_type} color="#7C3AED" testid="analytics-activity-types" />
        <BarListChart title="Lost deal reasons" data={data.lost_reasons} color="#E11D48" testid="analytics-lost-reasons" />
      </div>
    </div>
  );
}
